import chroma from "chroma-js";

import Select, { StylesConfig } from "react-select";
import { SelectInputOption } from "../../constants/common";
import { cn } from "../../utils/cn";
import { useTranslation } from "react-i18next";
import { memo } from "react";

interface SelectInputProps {
  selectedOptions: SelectInputOption[];
  setSelectedOptions: React.Dispatch<React.SetStateAction<SelectInputOption[]>>;
  error?: string;
  errorStyle?: string;
  label?: string;
  labelStyle?: string;
  required?: boolean;
  options: readonly SelectInputOption[];
}

const colourStyles: StylesConfig<SelectInputOption, true> = {
  control: (styles) => ({
    ...styles,
    backgroundColor: "white",
  }),
  option: (styles, { data, isDisabled, isFocused, isSelected }) => {
    const color = chroma(data.color);
    return {
      ...styles,
      backgroundColor: isDisabled
        ? undefined
        : isSelected
        ? data.color
        : isFocused
        ? color.alpha(0.1).css()
        : undefined,
      color: isDisabled
        ? "#ccc"
        : isSelected
        ? chroma.contrast(color, "white") > 2
          ? "white"
          : "black"
        : data.color,
      cursor: isDisabled ? "not-allowed" : "default",

      ":active": {
        ...styles[":active"],
        backgroundColor: !isDisabled
          ? isSelected
            ? data.color
            : color.alpha(0.3).css()
          : undefined,
      },
    };
  },
  multiValue: (styles, { data }) => {
    const color = chroma(data?.color);
    return {
      ...styles,
      backgroundColor: color.alpha(0.1).css(),
    };
  },
  multiValueLabel: (styles, { data }) => ({
    ...styles,
    color: data.color,
  }),
  multiValueRemove: (styles, { data }) => ({
    ...styles,
    color: data.color,
    ":hover": {
      backgroundColor: data.color,
      color: "white",
    },
  }),
};

const SelectInput = ({
  selectedOptions,
  setSelectedOptions,
  error,
  errorStyle,
  label,
  labelStyle,
  required = false,
  options,
}: SelectInputProps) => {
  const { t } = useTranslation();

  return (
    <div className="relative flex flex-col gap-[3px]">
      {label && (
        <label className={cn("text-[13px] font-normal", labelStyle)}>
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <Select
        closeMenuOnSelect={false}
        isMulti
        options={options}
        styles={colourStyles}
        value={selectedOptions}
        placeholder={`${t("select")}...`}
        onChange={(selected) =>
          setSelectedOptions(selected as SelectInputOption[])
        }
        className="rounded-[15px] text-sm border-2 z-50 focus:!border-purple-secondary focus:ring-0 px-1 py-0.5"
      />
      {!!error && (
        <p
          className={cn(
            "text-red-600 text-[10px] mt-[1px] absolute -bottom-[15px] left-[2px] italic",
            errorStyle
          )}
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default memo(SelectInput);
